import type { EditorAPI } from '#packages/editorAPI';
import type { CompRef } from '@wix/document-services-types';
import constants from '#packages/constants';
import {
  AiImageGenerator,
  FIELD_ROLES,
  type ImageResolution,
  IMAGE_RESOLUTION_BY_FIELD_ROLE,
} from '@wix/editor-site-generator';
import { uploadImage } from './fetchUtils';
import {
  InjectionStrategy,
  type ImageDetailsTyped,
  type ImagesMapTyped,
} from '../types';
import experiment from 'experiment';
import { files } from '@wix/santa-editor-utils';

const getImageOptions = ({
  height,
  width,
}: ImageResolution): {
  fieldRole: FIELD_ROLES;
  resolution: {
    height?: number;
    width?: number;
  };
} => {
  if (width > height) {
    return getImageOptionsByFieldRole(FIELD_ROLES.CROPTYPE_LANDSCAPE);
  }
  if (height > width) {
    return getImageOptionsByFieldRole(FIELD_ROLES.CROPTYPE_PORTRAIT);
  }
  const fieldRole = FIELD_ROLES.CROPTYPE_ABSTRACT;
  return {
    fieldRole,
    resolution: IMAGE_RESOLUTION_BY_FIELD_ROLE[fieldRole],
  };
};

const getImageOptionsByFieldRole = (fieldRole: FIELD_ROLES) => ({
  fieldRole,
  resolution: IMAGE_RESOLUTION_BY_FIELD_ROLE[fieldRole],
});

export const getContainerComponents = (
  { components }: EditorAPI,
  sectionRef: CompRef,
) => {
  const { COMP_TYPES } = constants;
  return [
    ...components.get.byType(COMP_TYPES.SECTION, sectionRef),
    ...components.get.byType(
      COMP_TYPES.STRIP_CONTAINER_SLIDE_SHOW_SLIDE,
      sectionRef,
    ),
    ...components.get.byType(COMP_TYPES.COLUMN, sectionRef),
  ];
};

const getComponentImagesMap = (
  editorAPI: EditorAPI,
  compRefs: CompRef[],
  sectionName: string,
  injectionStrategy: InjectionStrategy,
): ImagesMapTyped => {
  const imagesMap = new Map<string, ImageDetailsTyped>();
  const { components } = editorAPI;

  compRefs.forEach((compRef) => {
    const imageSize = components.layout.get_size(compRef);
    const { fieldRole, resolution } = getImageOptions(imageSize);
    const { height, width } = resolution;

    imagesMap.set(compRef.id, {
      ...imageSize,
      height,
      width,
      fieldRole,
      displayName: `${sectionName}.${compRef.id}`,
      injectionStrategy,
    });
  });

  return imagesMap;
};

export const createPhotoImagesMap = (
  editorAPI: EditorAPI,
  sectionRef: CompRef,
  sectionName: string,
) => {
  const { components } = editorAPI;
  const wphotoRefs = components.get.byType(
    constants.COMP_TYPES.PHOTO,
    sectionRef,
  );

  const photoImagesMap = getComponentImagesMap(
    editorAPI,
    wphotoRefs,
    sectionName,
    InjectionStrategy.PHOTO,
  );

  return photoImagesMap;
};

export const createBackgroundImagesMap = (
  editorAPI: EditorAPI,
  sectionRef: CompRef,
  sectionName: string,
) => {
  const { components } = editorAPI;
  const containersRefs = getContainerComponents(editorAPI, sectionRef);
  const backgroundContainersRefs = containersRefs.filter((compRef: CompRef) => {
    const compDesign = components.design.get(compRef);
    return (
      compDesign.background?.mediaRef?.type === 'Image' ||
      compDesign.original?.background?.mediaRef?.type === 'Image'
    );
  });

  const backgroundImagesMap = getComponentImagesMap(
    editorAPI,
    backgroundContainersRefs,
    sectionName,
    InjectionStrategy.BACKGROUND,
  );

  return backgroundImagesMap;
};

const getAiImagesPromises = (
  aiImageGenerator: AiImageGenerator,
  imagesMap: ImagesMapTyped,
  sectionName: string,
  msId: string,
  msInstance: string,
  businessTerm: string,
  siteDescription: string,
): Promise<ImagesMapTyped | undefined>[] => {
  return imagesMap.size > 0
    ? [
        getAiImages({
          aiImageGenerator,
          sectionName,
          imagesMap,
          msId,
          msInstance,
          businessTerm,
          siteDescription,
        }),
      ]
    : [];
};

export const getAiUploadedImages = async ({
  editorAPI,
  allSections,
  msInstance,
  businessTerm,
  siteDescription,
}: {
  editorAPI: EditorAPI;
  allSections: CompRef[];
  msInstance: string;
  businessTerm: string;
  siteDescription: string;
}): Promise<[string, ImageDetailsTyped][]> => {
  const aiImageGenerator = new AiImageGenerator({
    isAiTextOnImageOpen: experiment.isOpen('se_sg_aiTextOnImage'),
  });
  const msId = editorAPI.generalInfo.getMetaSiteId();

  const sectionsImageData = allSections.map((sectionRef) => {
    const sectionName = editorAPI.sections.getSectionName(sectionRef);
    return {
      sectionName,
      photoImagesMap: createPhotoImagesMap(editorAPI, sectionRef, sectionName),
      backgroundImagesMap: createBackgroundImagesMap(
        editorAPI,
        sectionRef,
        sectionName,
      ),
    };
  });

  const aiImageResults = await Promise.all(
    sectionsImageData.flatMap(
      ({ sectionName, photoImagesMap, backgroundImagesMap }) => {
        const photoImagesPromises = getAiImagesPromises(
          aiImageGenerator,
          photoImagesMap,
          sectionName,
          msId,
          msInstance,
          businessTerm,
          siteDescription,
        );
        const backgroundImagesPromises = getAiImagesPromises(
          aiImageGenerator,
          backgroundImagesMap,
          sectionName,
          msId,
          msInstance,
          businessTerm,
          siteDescription,
        );
        return [...photoImagesPromises, ...backgroundImagesPromises];
      },
    ),
  );

  const uploadPromises = aiImageResults.flatMap((aiImages) =>
    aiImages
      ? Array.from(aiImages.entries()).map(([compId, imageDetails]) =>
          uploadImage(msInstance, compId, imageDetails, aiImages),
        )
      : [],
  );

  await Promise.all(uploadPromises);

  return mergeImagesMaps(aiImageResults);
};

const getAiImages = async ({
  aiImageGenerator,
  sectionName,
  imagesMap,
  msId,
  msInstance,
  businessTerm,
  siteDescription,
}: {
  aiImageGenerator: AiImageGenerator;
  sectionName: string;
  imagesMap: ImagesMapTyped;
  msId: string;
  msInstance: string;
  businessTerm: string;
  siteDescription: string;
}): Promise<ImagesMapTyped | undefined> => {
  const imageUploadHandlers = {
    imageUrlUploadFn: async (
      externalUrl: { name: string; url: string },
      tempMetaSiteInstance?: string,
    ) => {
      const response = await files.uploadSiteFileUsingExternalUrl(
        tempMetaSiteInstance,
        externalUrl,
      );
      return response.url;
    },
    fileUploadFn: async (file: File) => {
      const { mediaAuthToken } = window.editorModel;
      if (!mediaAuthToken) {
        throw new Error('mediaAuthToken is missing');
      }
      return (await files.uploadSiteFile(mediaAuthToken, file)).file_name;
    },
  };

  const imageGenerationParams = {
    msId,
    msInstance,
    businessTerm,
    siteDescription,
    onGenerationEvent: () => {}, // No-op for now, can be used for BI-logging
  };

  const aiImages = await aiImageGenerator.getImages({
    sectionName,
    imagesMap,
    imageGenerationParams,
    imageUploadHandlers,
  });

  return aiImages.size ? aiImages : undefined;
};

const mergeImagesMaps = (imageMaps: (ImagesMapTyped | undefined)[]) => {
  return imageMaps.reduce<[string, ImageDetailsTyped][]>(
    (acc, imageMap) =>
      imageMap ? [...acc, ...Array.from(imageMap.entries())] : acc,
    [],
  );
};

export const injectAiImages = (
  editorAPI: EditorAPI,
  aiImages: [string, ImageDetailsTyped][],
) => {
  aiImages.forEach(([compId, { staticUri, injectionStrategy }]) => {
    if (!staticUri) return;
    switch (injectionStrategy) {
      case InjectionStrategy.PHOTO:
        injectPhotoMedia(editorAPI, compId, staticUri);
        break;

      case InjectionStrategy.BACKGROUND:
        injectBackgroundMedia(editorAPI, compId, staticUri);
        break;

      default:
        break;
    }
  });
};

const injectBackgroundMedia = (
  editorAPI: EditorAPI,
  compId: string,
  staticUri: string,
) => {
  const compRef = editorAPI.components.get.byId(compId);
  const design = editorAPI.components.design.get(compRef);
  if (!design) return;

  editorAPI.components.design.update(
    compRef,
    {
      background: {
        ...design.background,
        mediaRef: {
          ...design.background?.mediaRef,
          uri: staticUri,
        },
      },
    },
    true,
  );
};

const injectPhotoMedia = (
  editorAPI: EditorAPI,
  compId: string,
  staticUri: string,
) => {
  const compRef = editorAPI.components.get.byId(compId);

  editorAPI.components.data.update(
    compRef,
    {
      uri: staticUri,
    },
    true,
  );
};
